/**
 * reseatBoneBind — the descriptor half of the ROTATIONAL unbind-adjust-rebind
 * (ADR-030; the sibling of recenterBoneBind, which moves a bind's POSITION).
 * ONE responsibility: rotate a bone's bind orientation about a world axis
 * through the bone's own bind origin, carrying its whole subtree RIGIDLY,
 * while the mesh stays exactly where it is.
 *
 * Why the subtree rides along (measured reasoning, ADR-030 §12): a clip drives
 * every tracked bone's LOCAL rotation, so at play a child's world orientation
 * is the clip's, not the bind's. Compensating the immediate child's local
 * (the recenter idiom for positions) would therefore be overwritten by any
 * clip that tracks it, and a tracked Head under a reseated Neck would wear
 * only its own share of the seating while an untracked one wore the neck's
 * share twice. Rotating the subtree instead — every descendant's world bind
 * rotated about the bone's origin, every descendant's LOCAL bind untouched —
 * gives one answer whether or not a descendant is tracked: each bone's flesh
 * wears the cumulative seating of its ancestors, exactly what the display
 * filter's hierarchy showed.
 *
 * The descriptor's own laws hold: worldBind is the IBM-derived truth, so
 * every subtree bone takes its rotated frame; the reseated bone's localBind
 * re-derives from its own change (local' = local · q⁻¹ · R · q — parent-
 * agnostic, so a folded wrapper above a root survives untouched); descendants
 * keep their localBind by construction. Re-gated by parseRigDescriptor — never
 * a hand-patched descriptor. The scene twin (reseatSceneBoneBind, adapter)
 * applies the same surgery to the nodes and inverse-bind matrices; the run
 * driver applies both together, never one.
 *
 * Fail-loud: an unknown bone or a non-finite rotation throws — a caller bug,
 * not a product outcome.
 *
 * Depends on: ir/rig-descriptor, ir/errors, math/quat, math/vec3, arm-space
 * (the WorldAxisRotation shape). Depended on by: the app session driver
 * (run-document, head seating) and the app's in-place seating commit.
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import {
  quatFromAxisAngle,
  quatInvertUnit,
  quatMultiply,
  quatNormalize,
  quatRotateVec3,
  type Quat,
} from '../math/quat.js';
import { vec3Add, vec3Length, vec3Sub, type Vec3Data } from '../math/vec3.js';

export class ReseatError extends KernelError {}

/** A rotation about a WORLD axis, the shape the seating derivations emit. */
export interface BindReseat {
  /** Unit world axis. */
  readonly axis: Vec3Data;
  readonly radians: number;
}

/** The bone and every descendant, by name — the rigid body a reseat moves. */
export function subtreeOf(rig: RigDescriptor, boneName: string): readonly string[] {
  const childrenOf = new Map<string, string[]>();
  for (const b of rig.bones) {
    if (b.parent === null) continue;
    const list = childrenOf.get(b.parent) ?? [];
    list.push(b.name);
    childrenOf.set(b.parent, list);
  }
  const out: string[] = [];
  const stack = [boneName];
  while (stack.length > 0) {
    const name = stack.pop()!;
    out.push(name);
    for (const child of childrenOf.get(name) ?? []) stack.push(child);
  }
  return out;
}

/**
 * Return a new descriptor with `boneName`'s bind ROTATED by `reseat` about
 * the bone's own bind origin, its subtree carried rigidly. Positions of the
 * bone itself and every bone outside the subtree are untouched; the rest
 * silhouette is untouched by construction once the scene twin recomputes the
 * inverse binds (that identity is the scene test's assertion, not this one's).
 */
export function reseatBoneBind(
  rig: RigDescriptor,
  boneName: string,
  reseat: BindReseat,
): RigDescriptor {
  const bone = rig.bones.find((b) => b.name === boneName);
  if (bone === undefined) {
    throw new ReseatError(`cannot reseat "${boneName}" — no such bone in the descriptor.`);
  }
  if (!Number.isFinite(reseat.radians)) {
    throw new ReseatError(
      `cannot reseat "${boneName}" — the angle is not finite (${reseat.radians}).`,
    );
  }
  const axisLength = vec3Length(reseat.axis);
  if (!(axisLength > 0) || !Number.isFinite(axisLength)) {
    throw new ReseatError(`cannot reseat "${boneName}" — the axis has no direction.`);
  }
  const axis = {
    x: reseat.axis.x / axisLength,
    y: reseat.axis.y / axisLength,
    z: reseat.axis.z / axisLength,
  };
  const R = quatFromAxisAngle(axis, reseat.radians);
  const origin = bone.worldBind.position;
  const subtree = new Set(subtreeOf(rig, boneName));

  const rotated = (b: RigBone): RigBone => ({
    ...b,
    worldBind: {
      ...b.worldBind,
      position: vec3Add(origin, quatRotateVec3(R, vec3Sub(b.worldBind.position, origin))),
      rotation: quatNormalize(quatMultiply(R, b.worldBind.rotation)),
    },
  });

  const bones = rig.bones.map((b) => {
    if (!subtree.has(b.name)) return b;
    const moved = rotated(b);
    if (b.name !== boneName) return moved; // descendants: world moves, local stays
    // The reseated bone's local re-derives from its own world change:
    // local' = local · q⁻¹ · q', parent-agnostic (a folded wrapper above a
    // root is preserved exactly, and for a bone under a bone this equals the
    // decomposition against the parent's worldBind).
    const q = quatNormalize(b.worldBind.rotation);
    const delta = quatMultiply(quatInvertUnit(q), moved.worldBind.rotation);
    const local: Quat = quatNormalize(quatMultiply(b.localBind.rotation, delta));
    return { ...moved, localBind: { ...b.localBind, rotation: local } };
  });
  return parseRigDescriptor({ ...rig, bones });
}
